// API client for habita.co backend
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

function getToken() {
  return localStorage.getItem('habita_token');
}

export function setToken(t) {
  if (t) localStorage.setItem('habita_token', t);
  else localStorage.removeItem('habita_token');
}

/**
 * Maps any error (network, fetch, backend, runtime) to a user-friendly Spanish message.
 * Never exposes technical noise like "Body is disturbed", "TypeError", stack traces, etc.
 *
 * Usage:
 *   try { await api.login(...) } catch (err) { setError(friendlyError(err)) }
 */
export function friendlyError(err, fallback = 'Hubo un problema procesando la solicitud. Intenta nuevamente.') {
  if (!err) return fallback;

  const status = err.status;
  const raw = (err.message || '').toString();
  const lower = raw.toLowerCase();
  const path = (err.path || '').toLowerCase();

  // URL-aware fallbacks — used when message is just a generic "Error 4xx"
  // (happens if backend body got eaten by proxy AND no X-Error-Message header).
  const isGeneric = /^error \d{3}$/i.test(raw.trim());
  if (isGeneric && status) {
    if (path.includes('/auth/login') && status === 401) return 'No pudimos iniciar sesión con esas credenciales.';
    if (path.includes('/auth/register') && status === 409) return 'Este correo ya está registrado. ¿Quieres iniciar sesión?';
    if (path.includes('/auth/register') && status === 422) return 'Revisa los datos: nombre, email válido y contraseña de al menos 6 caracteres.';
    if (path.includes('/auth/reset-password') && (status === 400 || status === 404)) return 'El enlace expiró o no es válido. Solicita uno nuevo.';
    if (path.includes('/auth/forgot-password')) return 'No pudimos enviar el correo. Intenta de nuevo en unos minutos.';
    if (path.includes('/properties') && status === 403) return 'Llegaste al límite de tu plan. Mejora tu plan para publicar más.';
    if (path.includes('/properties') && status === 409) return 'Ya tienes una publicación con esa misma dirección.';
    if (path.includes('/properties') && status === 422) return 'Revisa los datos de la propiedad. El precio o el área no es válido.';
    if (path.includes('/uploads') && (status === 413 || status === 422)) return 'La imagen es demasiado grande o no tiene un formato válido.';
  }

  // Patterns we NEVER want to show to the end user
  const technicalNoise = [
    'body is disturbed',
    'body stream',
    'body has already',
    'already read',
    'already consumed',
    'failed to fetch',
    'networkerror',
    'load failed',
    'aborted',
    'unexpected token',
    'unexpected end',
    'json.parse',
    'json parse',
    'cannot read',
    'is undefined',
    'is not defined',
    'is not a function',
    'cannot destructure',
    'typeerror',
    'syntaxerror',
    'referenceerror',
    'cors',
    'preflight',
    'cross-origin',
  ];

  const isTechnical = technicalNoise.some((p) => lower.includes(p));

  // Connection errors -> always friendly
  if (status === 0 || isTechnical) {
    return 'No pudimos conectar con el servidor. Revisa tu conexión e intenta de nuevo.';
  }

  // Status-based friendly mappings — but PREFER the backend's Spanish message if it's clean
  const backendMessage = !isTechnical && raw && raw.length < 280 ? raw : null;

  if (status === 400) {
    return backendMessage || 'Revisa los datos ingresados e intenta otra vez.';
  }
  if (status === 401) {
    // Login-specific: detect common patterns
    if (lower.includes('credenciales') || lower.includes('invalid') || lower.includes('inválid')) {
      return 'No pudimos iniciar sesión con esas credenciales.';
    }
    return backendMessage || 'Tu sesión expiró. Vuelve a iniciar sesión.';
  }
  if (status === 403) {
    return backendMessage || 'No tienes permisos para realizar esta acción.';
  }
  if (status === 404) {
    return backendMessage || 'No encontramos lo que buscabas.';
  }
  if (status === 409) {
    // Common: duplicate email, duplicate property
    if (lower.includes('email') || lower.includes('correo')) {
      return 'Este correo ya está registrado. ¿Quieres iniciar sesión?';
    }
    return backendMessage || 'Ya existe un registro con esos datos.';
  }
  if (status === 413) {
    return 'El archivo es demasiado grande. Usa imágenes de menos de 10 MB.';
  }
  if (status === 422) {
    return backendMessage || 'Revisa los datos ingresados e intenta otra vez.';
  }
  if (status === 429) {
    return 'Demasiadas solicitudes seguidas. Espera unos segundos e intenta de nuevo.';
  }
  if (status >= 500) {
    return 'Estamos teniendo problemas en nuestros servidores. Intenta en unos minutos.';
  }

  // Anything else: prefer backend Spanish message if clean, otherwise fallback
  return backendMessage || fallback;
}

// ---------------------------------------------------------------------------
// Pydantic 422 → Spanish field-level error parser
// ---------------------------------------------------------------------------

/**
 * Master field label registry — covers EVERY form in the app:
 * auth (register/login/reset), profile, inspection, contact agent, property
 * publish/edit.
 *
 * Each entry: { label, step?, hint? }.
 *   - label: human-readable Spanish name used in error messages.
 *   - step: optional wizard step (PublishProperty uses 1..5).
 *   - hint: optional helper text suggested for input under-labels.
 *
 * Adding a new form? Register its fields here so 422 responses are translated
 * automatically without per-form code.
 */
export const FIELD_LABELS = {
  // --- Auth & profile ---
  name:        { label: 'Nombre',     hint: 'Mínimo 2 caracteres.' },
  email:       { label: 'Email',      hint: 'Ingresa un correo válido.' },
  password:    { label: 'Contraseña', hint: 'Mínimo 6 caracteres.' },
  phone:       { label: 'Teléfono' },
  role:        { label: 'Rol' },
  agency:      { label: 'Agencia' },
  bio:         { label: 'Biografía' },
  avatar:      { label: 'Avatar' },
  token:       { label: 'Token' },

  // --- Inspection / contact ---
  message:     { label: 'Mensaje',    hint: 'Cuéntale algo al agente.' },
  date:        { label: 'Fecha' },
  time:        { label: 'Hora' },
  notes:       { label: 'Notas' },
  property_id: { label: 'Propiedad' },

  // --- Reports ---
  reason:      { label: 'Motivo' },
  details:     { label: 'Detalles',   hint: 'Máximo 500 caracteres.' },
  reporter_email: { label: 'Tu correo' },

  // --- Property publish/edit (steps 1..5) ---
  title:        { label: 'Título',       step: 2, hint: 'Mínimo 5 caracteres.' },
  address:      { label: 'Dirección',    step: 2, hint: 'Ingresa una dirección válida (calle y número).' },
  city:         { label: 'Ciudad',       step: 2, hint: 'Selecciona la ciudad o municipio.' },
  neighborhood: { label: 'Barrio',       step: 2 },
  department:   { label: 'Departamento', step: 2 },
  stratum:      { label: 'Estrato',      step: 2, hint: 'Debe estar entre 1 y 6.' },
  description:  { label: 'Descripción',  step: 3, hint: 'La descripción debe tener al menos 10 caracteres.' },
  beds:         { label: 'Habitaciones', step: 3, hint: 'Debe ser un número entre 0 y 20.' },
  baths:        { label: 'Baños',        step: 3, hint: 'Debe ser un número entre 0 y 20.' },
  parking:      { label: 'Parqueaderos', step: 3, hint: 'Debe ser un número entre 0 y 20.' },
  area:         { label: 'Área (m²)',    step: 3, hint: 'El área debe estar entre 10 y 5.000 m².' },
  year:         { label: 'Año de construcción', step: 3 },
  features:     { label: 'Características',     step: 3 },
  images:       { label: 'Fotos',        step: 4, hint: 'Sube al menos 1 foto.' },
  videos:       { label: 'Videos',       step: 4 },
  price:        { label: 'Precio',       step: 5, hint: 'El precio debe ser mayor a 0.' },
  rentPrice:    { label: 'Precio de arriendo', step: 5, hint: 'Ingresa el precio de arriendo mensual.' },
  operations:   { label: 'Modalidad',    step: 1 },
  prices:       { label: 'Precios',      step: 5 },
  ev_charger:   { label: 'Cargador VE',  step: 3 },
  admin_fee:    { label: 'Administración', step: 5 },
  type:         { label: 'Tipo de inmueble', step: 1, hint: 'Selecciona el tipo de inmueble.' },
  operation:    { label: 'Operación',    step: 1 },
};

// Backwards-compatibility alias — kept so older imports keep working.
export const PROPERTY_FIELDS = FIELD_LABELS;

function translatePydanticMessage(d, label) {
  const type = d.type || '';
  const ctx = d.ctx || {};
  const lowered = (d.msg || '').toLowerCase();
  switch (type) {
    case 'missing':
      return `${label} es obligatorio.`;
    case 'string_too_short':
      return `${label} debe tener al menos ${ctx.min_length} caracteres.`;
    case 'string_too_long':
      return `${label} no debe superar los ${ctx.max_length} caracteres.`;
    case 'greater_than_equal':
      return `${label} debe ser mayor o igual a ${ctx.ge}.`;
    case 'less_than_equal':
      return `${label} debe ser menor o igual a ${ctx.le}.`;
    case 'greater_than':
      return `${label} debe ser mayor a ${ctx.gt}.`;
    case 'less_than':
      return `${label} debe ser menor a ${ctx.lt}.`;
    case 'int_parsing':
    case 'int_type':
      return `${label} debe ser un número entero válido.`;
    case 'float_parsing':
    case 'float_type':
      return `${label} debe ser un número válido.`;
    case 'value_error':
      return `${label}: ${d.msg || 'valor no válido'}.`;
    case 'enum':
      return `${label} tiene un valor no permitido.`;
    case 'url_parsing':
    case 'url_type':
      return `${label} debe ser una URL válida.`;
    case 'value_error.email':
    case 'string_pattern_mismatch':
      return `${label} no tiene un formato válido.`;
    case 'literal_error':
      return `${label} tiene un valor no permitido.`;
    case 'bool_parsing':
    case 'bool_type':
      return `${label} debe ser verdadero o falso.`;
    case 'datetime_parsing':
    case 'datetime_type':
      return `${label} no es una fecha válida.`;
    default:
      // Email validation in Pydantic v2 surfaces as `value_error` with msg
      // containing 'valid email'.
      if (lowered.includes('valid email')) return `${label} no es un correo válido.`;
      return `${label}: ${d.msg || 'valor no válido'}.`;
  }
}

/**
 * Parses a thrown API error and extracts a list of per-field validation
 * errors in Spanish. Returns null when the error is not a 422 with a
 * Pydantic detail array.
 *
 * Returned shape: [{ field, step, label, message, hint }]
 */
export function parseValidationErrors(err) {
  if (err?.status !== 422) return null;
  const detail = err?.data?.detail;
  if (!Array.isArray(detail) || !detail.length) return null;

  return detail
    .map((d) => {
      const path = Array.isArray(d.loc) ? d.loc.filter((x) => x !== 'body') : [];
      const field = path[0];
      if (!field) return null;
      const info = FIELD_LABELS[field] || { label: field, step: null };
      return {
        field,
        step: info.step,
        label: info.label,
        message: translatePydanticMessage(d, info.label),
        hint: info.hint,
      };
    })
    .filter(Boolean);
}

/**
 * Safe request wrapper:
 *  - Reads response body ONCE via .text(), parses JSON manually.
 *    Avoids "Body is disturbed or locked" (Safari) and "body stream already read" (Chrome/FF)
 *    that can happen when intermediaries (service workers, proxies, CDN compression)
 *    interfere with the readable stream.
 *  - Throws a tagged Error with .status and .data for callers.
 */
async function request(path, { method = 'GET', body, headers = {}, isForm = false, signal } = {}) {
  const opts = { method, headers: { ...headers } };
  if (signal) opts.signal = signal;
  const token = getToken();
  if (token) opts.headers['Authorization'] = `Bearer ${token}`;

  if (body && !isForm) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  } else if (body && isForm) {
    opts.body = body; // FormData — browser sets multipart boundary automatically
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, opts);
  } catch (netErr) {
    // Network/CORS/aborted — surface a friendlier error
    const err = new Error('No pudimos conectar con el servidor. Revisa tu conexión e intenta de nuevo.');
    err.status = 0;
    err.cause = netErr;
    throw err;
  }

  // Try the X-Error-Message header FIRST — works even when the body stream has
  // already been consumed by an upstream proxy (e.g. Cloudflare error-page injection).
  let headerMessage = null;
  try {
    const h = res.headers.get('x-error-message') || res.headers.get('X-Error-Message');
    if (h) headerMessage = decodeURIComponent(h);
  } catch { /* ignore */ }

  // Clone the response IMMEDIATELY so we own a private copy of the body stream.
  // Some preview environments (service workers, proxies, browser extensions, analytics
  // wrappers) consume the original response body before our code can read it, which
  // causes "Body is disturbed or locked" (Safari) or "body stream already read" (Chrome/FF).
  // The clone gives us a fresh, independent ReadableStream.
  let raw = '';
  try {
    const cloned = res.clone();
    raw = await cloned.text();
  } catch {
    // If clone+text fails, try once more on the original (best effort)
    try { raw = await res.text(); } catch { raw = ''; }
  }

  let data = null;
  if (raw) {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try { data = JSON.parse(raw); } catch { data = raw; }
    } else {
      // Some backends return JSON with text/plain headers — try anyway
      try { data = JSON.parse(raw); } catch { data = raw; }
    }
  }

  if (!res.ok) {
    // Preferred order: backend body detail > X-Error-Message header > generic
    let message = `Error ${res.status}`;
    if (data) {
      if (typeof data === 'string') message = data;
      else if (data.detail) {
        if (typeof data.detail === 'string') message = data.detail;
        else if (Array.isArray(data.detail) && data.detail.length) {
          message = data.detail.map((e) => e.msg || e.loc?.join('.') || JSON.stringify(e)).join(' · ');
        } else message = JSON.stringify(data.detail);
      } else if (data.message) message = data.message;
    } else if (headerMessage) {
      // Body was consumed by upstream proxy but we recovered the friendly text from header.
      message = headerMessage;
    }
    // Attach URL for friendlyError() to make URL-aware fallbacks (mitigation C)
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    err.path = path;
    throw err;
  }
  return data;
}

export const api = {
  // Auth
  register: (payload) => request('/auth/register', { method: 'POST', body: payload }),
  // Iter 44 · Privacy Center + Ley 1581
  privacyPolicyInfo: () => request('/privacy/policy-info'),
  privacyMyConsents: () => request('/privacy/consent/me'),
  privacyRecordConsent: (payload) => request('/privacy/consent', { method: 'POST', body: payload }),
  privacyExportMyData: () => request('/privacy/me/export'),
  privacyRequestDeletion: (payload) => request('/privacy/me/delete', { method: 'POST', body: payload }),
  privacyDeletionStatus: () => request('/privacy/me/delete'),
  privacyCancelDeletion: () => request('/privacy/me/delete', { method: 'DELETE' }),
  privacySubmitHabeasData: (payload) => request('/privacy/habeas-data', { method: 'POST', body: payload }),
  privacyAdminHabeasList: (status) => request(`/privacy/admin/habeas-data${status ? `?status=${status}` : ''}`),
  privacyAdminAuditLog: (limit = 100) => request(`/privacy/admin/audit-log?limit=${limit}`),
  login: (payload) => request('/auth/login', { method: 'POST', body: payload }),
  me: () => request('/auth/me'),
  updateMe: (payload) => request('/auth/me', { method: 'PATCH', body: payload }),
  forgotPassword: (email) => request('/auth/forgot-password', { method: 'POST', body: { email } }),
  resetPassword: (token, password) => request('/auth/reset-password', { method: 'POST', body: { token, password } }),

  // Properties
  listProperties: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined)).toString();
    return request(`/properties${qs ? '?' + qs : ''}`);
  },
  getFeatured: (limit = 6) => request(`/properties/featured?limit=${limit}`),
  getRecent: (limit = 8) => request(`/properties/recent?limit=${limit}`),
  getPlatformStats: () => request('/stats/platform'),
  getProperty: (id) => request(`/properties/${id}`),
  myProperties: () => request('/properties/mine'),
  createProperty: (payload) => request('/properties', { method: 'POST', body: payload }),
  updateProperty: (id, payload) => request(`/properties/${id}`, { method: 'PATCH', body: payload }),
  deleteProperty: (id) => request(`/properties/${id}`, { method: 'DELETE' }),
  setPropertyStatus: (id, status) => request(`/properties/${id}/status`, { method: 'POST', body: { status } }),
  featureProperty: (id, days = 7) => request(`/properties/${id}/feature?days=${days}`, { method: 'POST' }),

  // Investment baselines (admin)
  listInvestmentBaselines: () => request('/admin/investment-baselines'),
  investmentHeuristicTable: () => request('/admin/investment-baselines/heuristic-table'),
  upsertInvestmentBaseline: (payload) => request('/admin/investment-baselines', { method: 'POST', body: payload }),
  deleteInvestmentBaseline: (payload) => request('/admin/investment-baselines', { method: 'DELETE', body: payload }),

  // Advisor key-location resolver (Ubicación Clave)
  resolveKeyLocation: (payload) => request('/advisor/key-location/resolve', { method: 'POST', body: payload }),

  // Pricing estimate (motor de valoración)
  estimatePricing: (payload) => request('/pricing/estimate', { method: 'POST', body: payload }),

  // Seller capture — valoración gratuita (/valorar-gratis)
  createSellerLead: (payload) => request('/seller-leads', { method: 'POST', body: payload }),
  valuationCapture: (payload) => request('/leads/valuation-capture', { method: 'POST', body: payload }),
  advisorCapture: (payload) => request('/leads/advisor-capture', { method: 'POST', body: payload }),
  adminListSellerLeads: (status) => request(`/admin/seller-leads${status ? `?status=${status}` : ''}`),
  adminPatchSellerLead: (id, data) => request(`/admin/seller-leads/${id}`, { method: 'PATCH', body: data }),

  // Distribution Center (Meta FB/IG + WhatsApp)
  distributionLogs: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return request(`/admin/distribution/logs${qs ? `?${qs}` : ''}`);
  },
  distributionStats: () => request('/admin/distribution/stats'),
  distributionSettings: () => request('/admin/distribution/settings'),
  saveDistributionSettings: (body) => request('/admin/distribution/settings', { method: 'POST', body }),
  clearDistributionCredentials: () => request('/admin/distribution/credentials', { method: 'DELETE' }),
  retryDistribution: (id) => request(`/admin/distribution/retry/${id}`, { method: 'POST' }),
  dispatchDistribution: (propertyId, overrides) => request(`/admin/distribution/dispatch/${propertyId}`, { method: 'POST', body: overrides || {} }),
  previewDistribution: (propertyId) => request(`/admin/distribution/preview/${propertyId}`),
  distributionLog: (id) => request(`/admin/distribution/log/${id}`),
  diagnoseDistribution: (propertyId, compareTo) => request(`/admin/distribution/diagnose/${propertyId}${compareTo ? `?compare_to=${compareTo}` : ''}`),
  distributionAbStats: (propertyId) => request(`/admin/distribution/ab-stats${propertyId ? `?property_id=${propertyId}` : ''}`),
  registerDistributionClick: (body) => request('/distribution/click', { method: 'POST', body }),
  shareDistribution: (propertyId, body) => request(`/admin/distribution/share/${propertyId}`, { method: 'POST', body }),
  telegramTest: () => request('/admin/distribution/telegram/test', { method: 'POST' }),
  metaAuthorizeUrl: () => request('/admin/distribution/meta/authorize-url'),
  metaPages: () => request('/admin/distribution/meta/pages'),
  metaSelectPage: (body) => request('/admin/distribution/meta/select-page', { method: 'POST', body }),
  metaTest: () => request('/admin/distribution/meta/test', { method: 'POST' }),
  metaHealthCheck: () => request('/admin/distribution/meta/health-check', { method: 'POST' }),
  metaDisconnect: () => request('/admin/distribution/meta/disconnect', { method: 'POST' }),
  linkedinAuthorizeUrl: () => request('/admin/distribution/linkedin/authorize-url'),
  linkedinOrganizations: () => request('/admin/distribution/linkedin/organizations'),
  linkedinSelectOrg: (body) => request('/admin/distribution/linkedin/select-org', { method: 'POST', body }),
  linkedinTest: () => request('/admin/distribution/linkedin/test', { method: 'POST' }),
  linkedinDisconnect: () => request('/admin/distribution/linkedin/disconnect', { method: 'POST' }),
  tiktokAuthorizeUrl: () => request('/admin/distribution/tiktok/authorize-url'),
  tiktokDisconnect: () => request('/admin/distribution/tiktok/disconnect', { method: 'POST' }),
  tiktokCreatorInfo: () => request('/admin/distribution/tiktok/creator-info'),
  tiktokCaption: (propertyId) => request(`/admin/distribution/tiktok/caption/${propertyId}`),
  tiktokPublish: (propertyId, body) => request(`/admin/distribution/tiktok/publish/${propertyId}`, { method: 'POST', body }),
  tiktokStatus: (publishId) => request(`/admin/distribution/tiktok/status/${publishId}`),

  // Pricing baselines (admin CRUD para market_pricing_baselines)
  listPricingBaselines: () => request('/admin/pricing-baselines'),
  pricingHeuristicTable: () => request('/admin/pricing-baselines/heuristic-table'),
  upsertPricingBaseline: (payload) => request('/admin/pricing-baselines', { method: 'POST', body: payload }),
  deletePricingBaseline: (payload) => request('/admin/pricing-baselines', { method: 'DELETE', body: payload }),

  // Constructor Projects — envío público + admin approval
  submitConstructorProject: (payload) => request('/constructor-projects', { method: 'POST', body: payload }),
  uploadConstructorDoc: async (file, kind = 'brochure') => {
    const url = `${API_BASE}/constructor-projects/upload-doc?kind=${encodeURIComponent(kind)}`;
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(url, { method: 'POST', body: form });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
    return data;
  },
  listConstructorProjects: (status) => request(`/admin/constructor-projects${status ? '?status=' + encodeURIComponent(status) : ''}`),
  getConstructorProject: (id) => request(`/admin/constructor-projects/${id}`),
  approveConstructorProject: (id) => request(`/admin/constructor-projects/${id}/approve`, { method: 'POST', body: {} }),
  rejectConstructorProject: (id, reason) => request(`/admin/constructor-projects/${id}/reject`, { method: 'POST', body: { reason } }),

  // Decision Layer (CONVERGE + ACT + calibration + score per property)
  saveFirmPick: (payload) => request('/advisor/decision/firm-pick', { method: 'POST', body: payload }),
  latestFirmPick: (contextId) => request(`/advisor/decision/firm-pick/latest?context_id=${encodeURIComponent(contextId)}`),
  deleteFirmPick: (contextId) => request(`/advisor/decision/firm-pick?context_id=${encodeURIComponent(contextId)}`, { method: 'DELETE' }),
  scoreForProperty: (payload) => request('/advisor/score-for-property', { method: 'POST', body: payload }),
  submitCalibration: (payload) => request('/advisor/calibration', { method: 'POST', body: payload }),

  // Upload
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return request('/uploads/image', { method: 'POST', body: fd, isForm: true });
  },
  deleteImage: (publicId, provider = 'cloudinary') =>
    request(`/uploads/image?public_id=${encodeURIComponent(publicId)}&provider=${provider}`, { method: 'DELETE' }),
  uploadConfig: () => request('/uploads/config'),

  // Messages / Inspections
  sendMessage: (payload) => request('/messages', { method: 'POST', body: payload }),
  scheduleInspection: (payload) => request('/inspections', { method: 'POST', body: payload }),
  myMessages: () => request('/messages/received'),
  myInspections: () => request('/inspections/scheduled'),

  // Favorites
  listFavorites: () => request('/favorites'),
  addFavorite: (id) => request(`/favorites/${id}`, { method: 'POST' }),
  removeFavorite: (id) => request(`/favorites/${id}`, { method: 'DELETE' }),

  // Plans / subs
  listPlans: () => request('/plans'),
  subscribe: (payload) => request('/subscriptions', { method: 'POST', body: payload }),
  currentSubscription: () => request('/subscriptions/current'),
  cancelSubscription: () => request('/subscriptions/cancel', { method: 'POST' }),
  invoices: () => request('/invoices'),

  // Admin
  adminStats: () => request('/admin/stats'),
  adminProperties: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined && v !== false)).toString();
    return request(`/admin/properties${qs ? '?' + qs : ''}`);
  },
  adminApprove: (id) => request(`/admin/properties/${id}/approve`, { method: 'POST' }),
  adminReject: (id, reason = '') => request(`/admin/properties/${id}/reject?reason=${encodeURIComponent(reason)}`, { method: 'POST' }),
  adminVerifyProperty: (id) => request(`/admin/properties/${id}/verify`, { method: 'POST' }),
  adminUnverifyProperty: (id) => request(`/admin/properties/${id}/unverify`, { method: 'POST' }),
  adminDeleteProperty: (id) => request(`/admin/properties/${id}`, { method: 'DELETE' }),
  adminUsers: () => request('/admin/users'),
  adminBan: (id) => request(`/admin/users/${id}/ban`, { method: 'POST' }),
  adminUnban: (id) => request(`/admin/users/${id}/unban`, { method: 'POST' }),
  adminVerify: (id) => request(`/admin/users/${id}/verify`, { method: 'POST' }),
  adminUnverify: (id) => request(`/admin/users/${id}/unverify`, { method: 'POST' }),
  adminReports: (status = 'pending') => request(`/admin/reports?status=${status}`),
  adminResolveReport: (id) => request(`/admin/reports/${id}/resolve`, { method: 'POST' }),
  adminDismissReport: (id) => request(`/admin/reports/${id}/dismiss`, { method: 'POST' }),
  adminDuplicates: () => request('/admin/duplicates'),
  adminDashboardOverview: () => request('/admin/dashboard/overview'),
  growthV2: (window = '30d') => request(`/admin/dashboard/growth-v2?window=${window}`),

  // ── CRM (Iter 38) ─────────────────────────────────────────────────
  crmListLeads: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') qs.append(k, v); });
    const s = qs.toString();
    return request(`/crm/leads${s ? `?${s}` : ''}`);
  },
  crmGetLead: (id) => request(`/crm/leads/${id}`),
  crmSmartContext: (id) => request(`/crm/leads/${id}/smart-context`),
  crmCreateLead: (payload) => request('/crm/leads', { method: 'POST', body: payload }),
  crmUpdateLead: (id, payload) => request(`/crm/leads/${id}`, { method: 'PATCH', body: payload }),
  crmAddNote: (id, text) => request(`/crm/leads/${id}/notes`, { method: 'POST', body: { text } }),
  crmAddTask: (id, payload) => request(`/crm/leads/${id}/tasks`, { method: 'POST', body: payload }),
  crmCompleteTask: (leadId, taskId, complete = true) => request(`/crm/leads/${leadId}/tasks/${taskId}?complete=${complete}`, { method: 'PATCH' }),
  crmAddActivity: (id, payload) => request(`/crm/leads/${id}/activities`, { method: 'POST', body: payload }),
  crmDashboard: () => request('/crm/dashboard'),
  crmImportHistorical: (days = 90) => request(`/crm/admin/import-historical?days=${days}`, { method: 'POST' }),

  // ── Integrations (Iter 39 · FASE 4B.1) ─────────────────────────
  integrationsProviders: () => request('/integrations/providers'),
  integrationsConnections: () => request('/integrations/connections'),
  integrationsConnect: (payload) => request('/integrations/connect', { method: 'POST', body: payload }),
  integrationsSync: (provider, entity) => request(`/integrations/${provider}/sync${entity ? `?entity=${entity}` : ''}`, { method: 'POST' }),
  integrationsDisconnect: (provider) => request(`/integrations/${provider}/disconnect`, { method: 'POST' }),
  integrationsLogs: (provider, limit = 30) => request(`/integrations/${provider}/logs?limit=${limit}`),
  // Iter 41 · FASE 4B.3 — Wasi + real adapters
  integrationsVerify: (provider, credentials) => request('/integrations/verify', { method: 'POST', body: { provider, credentials } }),
  integrationsUpdateSchedule: (provider, sync_schedule) => request(`/integrations/${provider}/schedule`, { method: 'PATCH', body: { sync_schedule } }),
  integrationsTestSync: (provider) => request(`/integrations/${provider}/test-sync`, { method: 'POST' }),

  // API Keys
  apiKeysList: () => request('/integrations/api-keys'),
  apiKeysCreate: (payload) => request('/integrations/api-keys', { method: 'POST', body: payload }),
  apiKeysRevoke: (id) => request(`/integrations/api-keys/${id}`, { method: 'DELETE' }),

  // ── Inventory Import (Iter 40 · FASE 4B.2 · Universal Import Engine) ─
  importSources: () => request('/integrations/import/sources'),
  importUpload: (sourceType, file) => {
    const fd = new FormData();
    fd.append('source_type', sourceType);
    fd.append('file', file);
    return request('/integrations/import/upload', { method: 'POST', body: fd, isForm: true });
  },
  importGetJob: (jobId) => request(`/integrations/import/${jobId}`),
  importSaveMapping: (jobId, mapping, defaults = {}) =>
    request(`/integrations/import/${jobId}/mapping`, { method: 'POST', body: { mapping, defaults } }),
  importDryRun: (jobId) => request(`/integrations/import/${jobId}/dry-run`, { method: 'POST' }),
  importExecute: (jobId) => request(`/integrations/import/${jobId}/execute`, { method: 'POST' }),
  importListJobs: (limit = 30) => request(`/integrations/import?limit=${limit}`),
  importDeleteJob: (jobId) => request(`/integrations/import/${jobId}`, { method: 'DELETE' }),

  // Tags
  tagsList: () => request('/tags'),
  tagsCreate: (payload) => request('/tags', { method: 'POST', body: payload }),
  tagsDelete: (id) => request(`/tags/${id}`, { method: 'DELETE' }),

  // Reports (public)
  reportProperty: (id, payload) => request(`/properties/${id}/report`, { method: 'POST', body: payload }),

  // News (curated press sources, server-validated)
  listNews: () => request('/news'),

  // Similar / nearby properties
  getSimilarProperties: (id, limit = 8) => request(`/properties/${id}/similar?limit=${limit}`),

  // Video upload to Cloudinary
  uploadVideo: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return request('/uploads/video', { method: 'POST', body: fd, isForm: true });
  },
  deleteVideo: (publicId) => request(`/uploads/video?public_id=${encodeURIComponent(publicId)}`, { method: 'DELETE' }),

  // Admin: welcome email backfill
  adminEmailConfig: () => request('/admin/email-config'),
  adminBackfillPreview: () => request('/admin/welcome-backfill/preview'),
  adminBackfillTest: (to, role = 'user') => request(`/admin/welcome-backfill/test?to=${encodeURIComponent(to)}&role=${role}`, { method: 'POST' }),
  adminBackfillRun: (dryRun = true, limit = 500) => request(`/admin/welcome-backfill/run?dry_run=${dryRun}&limit=${limit}`, { method: 'POST' }),

  // Admin: follow-up scheduler (Day 1 / Day 3 / Day 7)
  adminFollowupConfig: () => request('/admin/followups/config'),
  adminFollowupStats: () => request('/admin/followups/stats'),
  adminFollowupPreview: (day, limit = 10) => request(`/admin/followups/preview?day=${day}&limit=${limit}`),
  adminFollowupTemplate: (day, name = 'María González') => request(`/admin/followups/template?day=${day}&name=${encodeURIComponent(name)}`),
  adminFollowupRun: (dryRun = true, day = null, limit = 500) => {
    const qs = `dry_run=${dryRun}&limit=${limit}` + (day ? `&day=${day}` : '');
    return request(`/admin/followups/run?${qs}`, { method: 'POST' });
  },

  // Agency demo-request leads (commercial pipeline)
  createAgencyLead: (data) => request('/agency-leads', { method: 'POST', body: data }),  adminListAgencyLeads: (status) => request(`/admin/agency-leads${status ? `?status=${status}` : ''}`),
  adminPatchAgencyLead: (id, data) => request(`/admin/agency-leads/${id}`, { method: 'PATCH', body: data }),

  // Agencies (companies)
  listAgencies: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v != null && v !== '' && qs.set(k, v));
    return request(`/agencies?${qs.toString()}`);
  },
  agencySearch: (q = '', limit = 10) => request(`/agencies/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  getAgency: (slug) => request(`/agencies/${encodeURIComponent(slug)}`),
  getAgencyProperties: (slug, params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v != null && v !== '' && qs.set(k, v));
    return request(`/agencies/${encodeURIComponent(slug)}/properties?${qs.toString()}`);
  },
  getAgencyTeam: (slug) => request(`/agencies/${encodeURIComponent(slug)}/team`),
  createAgency: (data) => request('/agencies', { method: 'POST', body: data }),
  requestAgencyJoin: (agencyId) => request(`/agencies/${agencyId}/membership/request`, { method: 'POST' }),
  updateAgency: (agencyId, data) => request(`/agencies/${agencyId}`, { method: 'PATCH', body: data }),
  getAgencyDashboard: (agencyId) => request(`/agencies/${agencyId}/dashboard`),
  listAgencyMembers: (agencyId) => request(`/agencies/${agencyId}/members`),
  approveMember: (agencyId, userId) => request(`/agencies/${agencyId}/members/${userId}/approve`, { method: 'POST' }),
  rejectMember: (agencyId, userId) => request(`/agencies/${agencyId}/members/${userId}/reject`, { method: 'POST' }),
  removeMember: (agencyId, userId) => request(`/agencies/${agencyId}/members/${userId}`, { method: 'DELETE' }),
  patchMember: (agencyId, userId, data) => request(`/agencies/${agencyId}/members/${userId}`, { method: 'PATCH', body: data }),
  adminVerifyAgency: (agencyId, verified = true) => request(`/admin/agencies/${agencyId}/verify?verified=${verified}`, { method: 'POST' }),
  adminListAgencies: (verified) => request(`/admin/agencies${verified !== undefined ? `?verified=${verified}` : ''}`),

  // Agents (personal brand)
  listAgents: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v != null && v !== '' && qs.set(k, v));
    return request(`/agents?${qs.toString()}`);
  },
  searchAgents: (q = '', limit = 10) => request(`/agents/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  getAgent: (slug) => request(`/agents/${encodeURIComponent(slug)}`),
  getAgentProperties: (slug, params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v != null && v !== '' && qs.set(k, v));
    return request(`/agents/${encodeURIComponent(slug)}/properties?${qs.toString()}`);
  },
  getAgentMe: () => request('/agents/me'),
  updateAgentMe: (data) => request('/agents/me', { method: 'PATCH', body: data }),
  ensureAgentSlug: () => request('/agents/me/ensure-slug', { method: 'POST' }),
  adminVerifyAgent: (agentId, verified = true) => request(`/admin/agents/${agentId}/verify?verified=${verified}`, { method: 'POST' }),

  // Developers / Projects / Units (Iter 28 architecture + Iter 29 demo)
  listDevelopers: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v != null && v !== '' && qs.set(k, v));
    return request(`/developers?${qs.toString()}`);
  },
  getDeveloper: (slug) => request(`/developers/${encodeURIComponent(slug)}`),
  listProjects: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v != null && v !== '' && qs.set(k, v));
    return request(`/projects?${qs.toString()}`);
  },
  getProject: (slug) => request(`/projects/${encodeURIComponent(slug)}`),
  listProjectUnits: (projectId, params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v != null && v !== '' && qs.set(k, v));
    return request(`/projects/${projectId}/units?${qs.toString()}`);
  },
  getSupportedCountries: () => request('/countries/supported'),

  // Email campaigns (admin)
  campaignsAudience: () => request('/admin/campaigns/audience'),
  campaignsHistory: () => request('/admin/campaigns'),
  campaignsSend: (body) => request('/admin/campaigns/send', { method: 'POST', body }),

  // Analytics + Notifications + Activity Center (Phase 2B)
  getAgentMetrics: (days = 30) => request(`/agents/me/metrics?days=${days}`),
  getAgencyMetrics: (agencyId, days = 30) => request(`/agencies/${agencyId}/metrics?days=${days}`),
  getActivity: (scope = 'mine', agencyId) => {
    const qs = new URLSearchParams({ scope });
    if (agencyId) qs.set('agency_id', agencyId);
    return request(`/activity?${qs.toString()}`);
  },
  getNotifications: (onlyUnread = false) => request(`/notifications${onlyUnread ? '?only_unread=true' : ''}`),
  markNotificationRead: (id) => request(`/notifications/${id}/read`, { method: 'POST' }),
  markAllNotificationsRead: () => request('/notifications/read-all', { method: 'POST' }),
  getHabitaScore: (entityType, entityId) => request(`/score/${entityType}/${entityId}`),

  // Admin: property geocoding stats + backfill
  adminGeocodingStats: () => request('/admin/properties/geocoding-stats'),
  adminGeocodeBackfill: (dryRun = true, limit = 200, onlyMissing = true) =>
    request(`/admin/properties/geocode-backfill?dry_run=${dryRun}&limit=${limit}&only_missing=${onlyMissing}`, { method: 'POST' }),
  adminRegeocodeProperty: (id) => request(`/admin/properties/${id}/regeocode`, { method: 'POST' }),

  // Locations (Colombian DIVIPOLA municipalities)
  listMunicipalities: (q = '', limit = 8) => request(`/locations/municipalities?q=${encodeURIComponent(q)}&limit=${limit}`),

  // Fase 2A · Constructora (developer) — todo scopeado por get_current_developer
  developerMe: () => request('/developer/me'),
  updateDeveloperProfile: (body) => request('/developer/me', { method: 'PATCH', body }),
  developerSummary: () => request('/developer/me/summary'),
  developerProjects: (limit = 50, skip = 0) => request(`/developer/me/projects?limit=${limit}&skip=${skip}`),
  developerProject: (id) => request(`/developer/me/projects/${id}`),
  createDeveloperProject: (body) => request('/developer/me/projects', { method: 'POST', body }),
  updateDeveloperProject: (id, body) => request(`/developer/me/projects/${id}`, { method: 'PATCH', body }),

  // Fase 2B · Tipologías + unidades (self-service, aislamiento por developer)
  developerTypologies: (pid) => request(`/developer/me/projects/${pid}/typologies`),
  createDeveloperTypology: (pid, body) => request(`/developer/me/projects/${pid}/typologies`, { method: 'POST', body }),
  updateDeveloperTypology: (pid, tid, body) => request(`/developer/me/projects/${pid}/typologies/${tid}`, { method: 'PATCH', body }),
  deleteDeveloperTypology: (pid, tid) => request(`/developer/me/projects/${pid}/typologies/${tid}`, { method: 'DELETE' }),
  developerUnits: (pid, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/developer/me/projects/${pid}/units${qs ? `?${qs}` : ''}`);
  },
  createDeveloperUnit: (pid, body) => request(`/developer/me/projects/${pid}/units`, { method: 'POST', body }),
  updateDeveloperUnit: (pid, uid, body) => request(`/developer/me/projects/${pid}/units/${uid}`, { method: 'PATCH', body }),
  deleteDeveloperUnit: (pid, uid) => request(`/developer/me/projects/${pid}/units/${uid}`, { method: 'DELETE' }),
};

// Resolve image url: if relative, prepend backend
export function imgUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
}

// Resize-aware thumbnail helper. Handles both Cloudinary and Unsplash URLs.
// For Cloudinary: injects transformations (c_fill, w, h, q_auto, f_auto, g_auto)
// For Unsplash:   rewrites/inserts ?auto=format&fit=crop&w=X&q=75
// For others:     returns original (no transformation)
export function thumbUrl(url, { w = 600, h = 450, crop = 'fill' } = {}) {
  if (!url) return '';
  const full = imgUrl(url);

  // Cloudinary
  if (full.includes('res.cloudinary.com') && full.includes('/upload/')) {
    const params = `c_${crop},w_${w},h_${h},q_auto,f_auto,g_auto`;
    return full.replace('/upload/', `/upload/${params}/`);
  }

  // Unsplash — rewrite or add width param to leverage their CDN
  if (full.includes('images.unsplash.com/')) {
    const [base, query = ''] = full.split('?');
    const params = new URLSearchParams(query);
    params.set('auto', 'format');
    params.set('fit', 'crop');
    params.set('w', String(w));
    params.set('q', '75');
    return `${base}?${params.toString()}`;
  }

  return full;
}
